import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { localToday } from '../../platform/localDate'
import { emitCreated, emitDeleted, emitEdited, emitPaused } from './budgetAnalytics'
import {
  changedFields,
  toCreateBody,
  toEnvelope,
  toPatchBody,
  type Envelope,
  type EnvelopeDraft,
} from './budgetWire'

export const WRITE_COPY = {
  category: 'That category is no longer available. Choose another.',
  invalid: 'Check the name and amount, then try again.',
  gone: 'This envelope was removed on another device.',
  failed: "Couldn't save. Try again.",
} as const

export type BudgetWriteKind = keyof typeof WRITE_COPY

export class BudgetWriteError extends Error {
  readonly kind: BudgetWriteKind
  readonly status: number | null
  constructor(kind: BudgetWriteKind, status: number | null) {
    super(WRITE_COPY[kind])
    this.name = 'BudgetWriteError'
    this.kind = kind
    this.status = status
  }
}

/** A 422 with a STRING `detail` is the backend's category check; an array is pydantic's. */
export function classifyWriteError(error: unknown, status: number | null): BudgetWriteError {
  if (status === 404) return new BudgetWriteError('gone', status)
  if (status === 422) {
    const detail =
      typeof error === 'object' && error !== null && 'detail' in error ? (error as { detail: unknown }).detail : undefined
    if (typeof detail === 'string' && detail.startsWith('Unknown or inaccessible category')) {
      return new BudgetWriteError('category', status)
    }
    if (Array.isArray(detail)) return new BudgetWriteError('invalid', status)
  }
  return new BudgetWriteError('failed', status)
}

/** Anything thrown by the request itself (network) or by parsing the answer is `failed`. */
async function guard<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run()
  } catch (cause) {
    throw cause instanceof BudgetWriteError ? cause : new BudgetWriteError('failed', null)
  }
}

function useInvalidate() {
  const client = useQueryClient()
  return () => client.invalidateQueries({ queryKey: keys.budgets.all() })
}

export function useCreateBudget(): UseMutationResult<Envelope, BudgetWriteError, EnvelopeDraft> {
  const invalidate = useInvalidate()
  return useMutation<Envelope, BudgetWriteError, EnvelopeDraft>({
    mutationFn: (draft) =>
      guard(async () => {
        const { data, error, response } = await api.POST('/api/v1/budgets', {
          body: toCreateBody(draft, localToday()),
        })
        if (error !== undefined || data === undefined) throw classifyWriteError(error, response.status)
        return toEnvelope(data)
      }),
    onSuccess: (_created, draft) => {
      emitCreated(draft.categoryId !== null)
      return invalidate()
    },
  })
}

export function useUpdateBudget(): UseMutationResult<
  Envelope,
  BudgetWriteError,
  { before: Envelope; draft: EnvelopeDraft }
> {
  const invalidate = useInvalidate()
  return useMutation<Envelope, BudgetWriteError, { before: Envelope; draft: EnvelopeDraft }>({
    mutationFn: ({ before, draft }) =>
      guard(async () => {
        const body = toPatchBody(before, draft)
        if (Object.keys(body).length === 0) return before
        const { data, error, response } = await api.PATCH('/api/v1/budgets/{budget_id}', {
          params: { path: { budget_id: before.id } },
          body,
        })
        if (error !== undefined || data === undefined) throw classifyWriteError(error, response.status)
        return toEnvelope(data)
      }),
    onSuccess: (_updated, { before, draft }) => {
      emitEdited(changedFields(before, draft))
      return invalidate()
    },
  })
}

export function useSetBudgetActive(): UseMutationResult<
  Envelope,
  BudgetWriteError,
  { id: string; active: boolean }
> {
  const invalidate = useInvalidate()
  return useMutation<Envelope, BudgetWriteError, { id: string; active: boolean }>({
    mutationFn: ({ id, active }) =>
      guard(async () => {
        const { data, error, response } = await api.PATCH('/api/v1/budgets/{budget_id}', {
          params: { path: { budget_id: id } },
          body: { is_active: active },
        })
        if (error !== undefined || data === undefined) throw classifyWriteError(error, response.status)
        return toEnvelope(data)
      }),
    onSuccess: (_updated, { active }) => {
      emitPaused(active)
      return invalidate()
    },
  })
}

export function useDeleteBudget(): UseMutationResult<void, BudgetWriteError, { id: string }> {
  const invalidate = useInvalidate()
  return useMutation<void, BudgetWriteError, { id: string }>({
    mutationFn: ({ id }) =>
      guard(async () => {
        const { error, response } = await api.DELETE('/api/v1/budgets/{budget_id}', {
          params: { path: { budget_id: id } },
        })
        if (error !== undefined) throw classifyWriteError(error, response.status)
      }),
    onSuccess: () => {
      emitDeleted()
      return invalidate()
    },
  })
}
